import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { fonts, colors, links } from '../common/mixins.js';
import Icon from '../common/Icon.jsx';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled as muiStyled } from '@mui/system';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import PrintTickets from './PrintTickets.jsx';
import ReleaseTickets from './ReleaseTickets.jsx';
import { printTickets, printProducts } from "../../utils/PrintUtils.js";
import PrintProducts from './PrintProducts.jsx';
import useModal from "../../hooks/useModal";
import { createPortal } from "react-dom";
import Overlay from "../common/Overlay";

const theme = createTheme({
    components: {
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent', 
                    backdropFilter: 'none',

                    '&.MuiBackdrop-invisible': {
                        backgroundColor: 'transparent',
                        backdropFilter: 'none'
                    }
                }
            }
        }
    }
});

const StyledMenu = muiStyled(Menu)`
  & .MuiPaper-root {
    border-radius: 8px;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.24);
    margin-top: 8px;
    padding:8px;
    display:flex;
    flex-direction:column;
    gap:8px;
  }
  & .MuiList-root {
    padding-top: 0;
    padding-bottom: 0;
  }
  & .MuiButtonBase-root {
    font-size: 14px;
  }
`;

const StyledMenuItem = muiStyled(MenuItem)(({ isMobile }) => `
${fonts.darkPurpleText()};
display:flex;
align-items:center;
gap:4px;
min-width:142px;
padding: 4px 8px;
border-radius:8px;
margin: 0;
height: 40px;
min-height: auto;
&:last-child {
    margin-bottom: 0;
}
margin-bottom: 8px;
 &:focus {
    background-color: transparent;
  }
  &:hover {
    background-color: ${colors.lightPrimary};
  }
`);

const StyledLink = styled.a`
  ${links.linkSmallBold()};
  line-height: 20px;
  cursor: pointer;
  &:hover {
    color: #502CB5;
    text-decoration: none;
  }
`;

const ManageYourOrder = ({ hasTickets, hasProducts, tickets = [], products = [], isMobile, travelBaseUrl, tellerBaseUrl, hasReleaseSeat })=> {
    const [anchorEl, setAnchorEl] = useState(null);
    const [content, setContent] = useState(null);
    const [successPopupVisible, setSuccessPopupVisible] = useState(false);
    const open = Boolean(anchorEl);
    const { isOpen, openModal, closeModal } = useModal();
    const filteredTickets = tickets.filter(ticket => ticket.status !== "CN");
    const filteredProducts = products.filter(product => product.status !== "CN");

    const handleClick = (event) => {
        if (hasReleaseSeat) {
            setAnchorEl(event.currentTarget);  
        } else {
            if (filteredTickets.length > 0 && filteredTickets.length === 1 && filteredProducts.length === 0) {
                const contentId = document.getElementById('OrderContentId').value;

                //Print Single Ticket Directly
                printTickets(filteredTickets, travelBaseUrl, contentId);
                
                return;
            }
            
            // If only tickets exist, open tickets modal
            if (hasTickets > 0 && hasProducts === 0) {
                setContent('tickets');
                openModal();
                return;
            }
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleOpenModal = (e,content) => {
        e.preventDefault();
        setAnchorEl(null);
        setContent(content);
        openModal();
    };
    const handleCloseModal = () => {
        setAnchorEl(null);
        setContent(null);
        closeModal();
    };

    const closeModalAfterReleaseSeat= () => {
        setAnchorEl(null);
        setContent(null);
        closeModal();
        setSuccessPopupVisible(true);
        setTimeout(() => setSuccessPopupVisible(false), 3000);
    }

    const getPrintText = (hasTickets, hasProducts) => {
        if (hasReleaseSeat) {
            return orderdetails_resources.ManageYourOrder
        } else {
            if (hasTickets > 0 && hasProducts > 0) {
                return orderdetails_resources.Print; // when there are tickets+products
            } else if (hasTickets > 0) {
                return orderdetails_resources.PrintTickets; // only tickets
            } else if (hasProducts > 0) {
                return orderdetails_resources.PrintProducts; // only products
            }
            return orderdetails_resources.Print; 
        }
    };

    const getIdText = (hasTickets, hasProducts) => {
        if (hasTickets > 0 && hasProducts > 0) {
            return 'print'; // when there are tickets+products
        } else if (hasTickets > 0) {
            return 'printTickets' // only tickets
        } else if (hasProducts > 0) {
            return 'printProducts' // only products
        }
        return 'print'; 
    };
    const handleScroll = () => {
        if (anchorEl) {
            setAnchorEl(null);
        }
    }; 

    useEffect(() => {

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };

    }, [anchorEl]);

      const SuccessPopup = ({ message }) =>
        createPortal(
          <div
            style={{
              position: "fixed",
              ...(!isMobile && { top: "32px" }),
              ...(isMobile && { bottom: "32px" }),
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#28BE63",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontFamily: "pf_highway_sans_pro_lightRg",
              zIndex: 1000,
              whiteSpace: "nowrap",
            }}
          >
            {message}
          </div>,
          document.body
        );

    return (
        <>
            <ThemeProvider theme={theme}>
                <CssBaseline />
            <div>     
                    {isMobile ? <a onClick={handleClick}><Icon kind="dots"/></a>
                        : <StyledLink id={getIdText(hasTickets, hasProducts)} onClick={handleClick}>{getPrintText(hasTickets, hasProducts)}</StyledLink>}
                    {((hasTickets > 0 && hasProducts > 0) || hasReleaseSeat) && (
                <StyledMenu
                    id="manage-your-order-menu"
                    anchorEl={anchorEl}
                    open={open}
                    disableScrollLock
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >       
                    {hasReleaseSeat && <StyledMenuItem id="ReleaseSeatModalOpt" isMobile={isMobile}  onClick={(e)=>handleOpenModal(e,'release_seat')}>
                            <Icon kind="seat" />
                            {orderdetails_resources.ReleaseSeat}
                        </StyledMenuItem>}
                            {hasTickets > 0 && <StyledMenuItem id="TicketsModalOpt" isMobile={isMobile}  onClick={(e)=>handleOpenModal(e,'tickets')}>
                            <Icon kind="print" />
                            <span id="printTickets">{isMobile ? orderdetails_resources.DownloadTickets : orderdetails_resources.PrintTickets}</span>
                        </StyledMenuItem>}
                            {hasProducts > 0 && <StyledMenuItem id="ProductsModalOpt"  isMobile={isMobile}  onClick={(e)=>handleOpenModal(e,'products')}>
                            <Icon kind="print" />
                            <span id="printProducts">{isMobile ? orderdetails_resources.DownloadProducts : orderdetails_resources.PrintProducts}</span>
                        </StyledMenuItem>}
                </StyledMenu> 
                    )}
                </div>

            </ThemeProvider>
            <Overlay
            handleClose={handleCloseModal}
            overflowHidden={content === 'release_seat' ? true : false}
            open={isOpen}
            id={`print-${content}-modal`}
            size="sm"
                titleText={
                    content === "release_seat" ? releaseseatmodal_resources.ReleaseSeatTitle :
                    content === 'tickets'
                        ? (isMobile ? orderdetails_resources.DownloadTickets : orderdetails_resources.PrintYourTickets)
                        : (isMobile ? orderdetails_resources.DownloadProducts : orderdetails_resources.PrintProducts)
                }
            >
                {content === 'tickets' && <PrintTickets tickets={filteredTickets} travelBaseUrl={travelBaseUrl} />}
                {content === 'products' && <PrintProducts products={filteredProducts} tellerBaseUrl={tellerBaseUrl} />}
                {content === 'release_seat' && <ReleaseTickets tickets={filteredTickets} travelBaseUrl={travelBaseUrl} callBack={closeModalAfterReleaseSeat}/>}
            </Overlay>
            {successPopupVisible && (
                <SuccessPopup message={releaseseatmodal_resources.ReleaseSeatSuccess} />
            )}
        </>
    );
};

export default ManageYourOrder;
